import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, ArrowUpRight } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';

const Footer = () => {
  const currentYear = new Date().getFullYear();
  const { t } = useLanguage();

  // Map service paths to translated names from context
  const serviceLinks = [
    { path: '/services/construction', name: t.services.items[0].name },
    { path: '/services/renovation',   name: t.services.items[1].name },
    { path: '/services/piscine',      name: t.services.items[4].name },
    { path: '/services/exterieur',    name: t.services.items[3].name },
    { path: '/services/maconnerie',   name: t.services.items[5].name },
    { path: '/services/interieur',    name: t.services.items[2].name },
  ];

  return (
    <footer style={{ backgroundColor: '#006039' }} className="text-white" data-testid="footer">

      {/* Orange accent rule at top */}
      <div style={{ height: '3px', backgroundColor: '#E87722' }} />

      {/* Main grid */}
      <div className="max-w-[1800px] mx-auto px-6 md:px-12 lg:px-20">
        <div className="pt-16 md:pt-20 pb-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12">

          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="inline-block mb-6">
              <div className="flex flex-col leading-none">
                <span className="text-[9px] tracking-[0.45em] uppercase font-medium text-white/50 -mb-0.5">
                  Construcciones
                </span>
                <span className="font-heading text-[28px] text-white tracking-tight">
                  REMALEX
                </span>
              </div>
            </Link>
            <p className="text-white/50 text-sm leading-relaxed mb-8 max-w-[220px]">
              {t.footer.tagline}
            </p>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-[10px] font-bold tracking-[0.35em] uppercase mb-6" style={{ color: '#E87722' }}>
              {t.footer.servicesTitle}
            </h3>
            <ul className="space-y-3">
              {serviceLinks.map((s) => (
                <li key={s.path}>
                  <Link
                    to={s.path}
                    className="text-white/50 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Navigation */}
          <div>
            <h3 className="text-[10px] font-bold tracking-[0.35em] uppercase mb-6" style={{ color: '#E87722' }}>
              {t.footer.navTitle}
            </h3>
            <ul className="space-y-3">
              {t.footer.navLinks.map((l) => (
                <li key={l.to}>
                  <Link
                    to={l.to}
                    className="text-white/50 hover:text-white transition-colors duration-200 text-sm"
                  >
                    {l.label}
                  </Link>
                </li>
              ))}
              <li>
                <Link
                  to="/mentions-legales"
                  className="text-white/50 hover:text-white transition-colors duration-200 text-sm"
                >
                  {t.footer.legal}
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-[10px] font-bold tracking-[0.35em] uppercase mb-6" style={{ color: '#E87722' }}>
              {t.footer.contactTitle}
            </h3>
            <ul className="space-y-5">
              <li className="flex items-start gap-3">
                <MapPin className="w-3.5 h-3.5 flex-shrink-0 mt-0.5" style={{ color: '#E87722' }} />
                <span className="text-white/50 text-sm leading-relaxed">
                  C. Avets 5 "Casa Aria"<br />
                  E-17250 Calonge<br />
                  Costa Brava, España
                </span>
              </li>
              <li className="flex items-center gap-3">
                <Phone className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#E87722' }} />
                <a
                  href="tel:+34602645005"
                  className="text-white/50 hover:text-white transition-colors duration-200 text-sm"
                >
                  +34 602 645 005
                </a>
              </li>
              <li className="flex items-center gap-3">
                <Mail className="w-3.5 h-3.5 flex-shrink-0" style={{ color: '#E87722' }} />
                <a
                  href="mailto:info@construcciones-remalex.com"
                  className="text-white/50 hover:text-white transition-colors duration-200 text-sm"
                >
                  info@construcciones-remalex.com
                </a>
              </li>
            </ul>

            {/* CTA */}
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 mt-8 text-[11px] font-semibold tracking-[0.2em] uppercase h-10 px-5 border border-white/30 text-white hover:bg-white hover:text-forest transition-all duration-300"
            >
              {t.nav.cta}
              <ArrowUpRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        </div>

        {/* Orange divider */}
        <div style={{ height: '1px', backgroundColor: '#E87722', opacity: 0.35 }} />

        {/* Copyright bar */}
        <div
          className="py-5 flex flex-col md:flex-row items-center justify-between gap-3"
        >
          <p className="text-white/35 text-xs">
            © {currentYear} Construcciones Remalex S.L. · NIF/CIF B44979003 · {t.footer.rights}
          </p>
          <p className="text-white/20 text-xs">
            {t.footer.developedBy}{' '}
            <a
              href="https://www.solenca.es"
              target="_blank"
              rel="noopener noreferrer"
              className="hover:text-white/50 transition-colors"
            >
              Solenca Studio
            </a>
          </p>
          <div className="flex items-center gap-6 text-xs">
            <Link
              to="/mentions-legales"
              className="text-white/35 hover:text-white/70 transition-colors"
            >
              {t.footer.legal}
            </Link>
            <Link
              to="/politique-confidentialite"
              className="text-white/35 hover:text-white/70 transition-colors"
            >
              {t.footer.privacy}
            </Link>
            <button
              onClick={() => window.dispatchEvent(new CustomEvent('open-cookie-prefs'))}
              className="text-white/35 hover:text-white/70 transition-colors"
            >
              {t.footer.cookies || 'Cookies'}
            </button>
            <Link
              to="/login"
              className="text-white/20 hover:text-white/50 transition-colors"
              title={t.footer.adminTooltip}
            >
              {t.footer.adminLabel}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
